import React, { useEffect, useRef } from 'react'
import {
  Button,
  FormText,
  Input,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap'
import { X } from 'react-feather'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'

import { Show } from '~/components/commons'

const SUPPORTED_SOCIAL_MEDIA_REGEX = /facebook|tiktok|instagram|pinterest|linkedin|twitter|youtube/

const SOCIAL_MEDIA_HELPER = {
  facebook: 'https://www.facebook.com/{username}',
  instagram: 'https://www.instagram.com/{username}',
  linkedin:
    'https://www.linkedin.com/in/{username}, https://www.linkedin.com/company/{username}',
  pinterest: 'https://www.pinterest.com/{username}',
  tiktok: 'https://www.tiktok.com/@{username}',
  twitter: 'https://www.twitter.com/{username}',
  youtube:
    'https://www.youtube.com/channel/{username}, https://www.youtube.com/user/{username}, https://www.youtube.com/@{username}',
}

const SOCIAL_MEDIA_REGEX = {
  facebook: /((http(s)?:\/\/)?(www\.)?)?facebook\.com\/.{1,}/,
  instagram: /(https:\/\/)?www\.instagram\.com\/.{1,}/,
  linkedin: /((http(s)?:\/\/)?(www\.)?)?([a-z]{2}\.)?linkedin\.com\/(in|company)\/.{1,}/,
  pinterest: /((http(s)?:\/\/)?(www\.)?)?([a-z]{2}\.)?pinterest\.com\/.{1,}/,
  tiktok: /((http(s)?:\/\/)?(www\.)?)?tiktok\.com\/@.{1,}/,
  twitter: /((http(s)?:\/\/)?(www\.)?)?(twitter|x)\.com\/.{1,}/,
  youtube: /((http(s)?:\/\/)?(www\.)?)?youtube\.com\/(channel\/|user\/|@).{1,}/,
}

const validateUrl = urlString => {
  try {
    return Boolean(new URL(urlString))
  } catch (e) {
    return false
  }
}

function URLAttributeInput({
  value: urls = [],
  onChange,
  disabled,
  id,
  onChangeRemoveURLs,
  editableFields,
  switchId,
  ...props
}) {
  const urlInputRef = useRef(null)
  const [isSocialMedia, setIsSocialMedia] = React.useState(false)
  const [regex, setRegex] = React.useState(null)
  const [helperText, setHelperText] = React.useState(null)
  const [error, setError] = React.useState('')

  function handleAddURL() {
    const newValue = urlInputRef?.current?.value
    const isValidUrl = validateUrl(newValue)
    const isSocialValid = isSocialMedia && regex.test(newValue)
    // The URL is not valid or was already added!
    if (!newValue) return setError("URL can't be empty!")

    if (!isValidUrl) return setError('Invalid URL!')

    if (isSocialMedia && !isSocialValid)
      return setError('Invalid URL for this social media!')

    onChange([newValue])
    urlInputRef.current.value = ''
  }

  function handleChangeURL() {
    if (error) setError(false)
  }

  function handleRemoveURL(id) {
    onChange(urls.filter((_, index) => id !== index))
  }

  useEffect(() => {
    if (editableFields[switchId]) {
      onChangeRemoveURLs(prevState => [
        ...prevState,
        switchId.replace('Switch', ''),
      ])
    } else {
      onChangeRemoveURLs(prevState =>
        prevState.filter(id => id !== switchId.replace('Switch', ''))
      )
    }
  }, [editableFields, switchId, onChangeRemoveURLs])

  useEffect(() => {
    if (!id) return

    const parsedId = id.replace('attributes/url_', '')
    const isSocialMediaAttribute = SUPPORTED_SOCIAL_MEDIA_REGEX.test(parsedId)

    if (isSocialMediaAttribute) {
      const newHelperText = SOCIAL_MEDIA_HELPER[parsedId]
      const regex = SOCIAL_MEDIA_REGEX[parsedId]

      setIsSocialMedia(true)
      setHelperText(newHelperText)
      setRegex(regex)
    }
  }, [id])

  return (
    <div style={{ marginBottom: 5 }}>
      <InputGroup
        onKeyPress={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleAddURL()
          }
        }}
      >
        <Input
          invalid={error}
          innerRef={urlInputRef}
          className="form-control"
          onChange={handleChangeURL}
          type="text"
          disabled={disabled}
          id={id}
          {...props}
        />
        <InputGroupAddon addonType="append">
          <Show condition={!disabled}>
            <Button
              onClick={handleAddURL}
              type="button"
              color="primary"
              disabled={disabled}
              {...props}
            >
              <FormattedMessage id="Add" />
            </Button>
          </Show>
        </InputGroupAddon>
      </InputGroup>
      <Show condition={!disabled}>
        {helperText && <FormText>{helperText}</FormText>}
        {Boolean(error) && (
          <>
            <ErrorText>
              <FormattedMessage id={error} />
            </ErrorText>
          </>
        )}
        <Show condition={urls.length > 0}>
          <URLsContainer>
            {urls?.map((url, index) => (
              <div key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
                <Button color="link" onClick={() => handleRemoveURL(index)}>
                  <X size={15} color="#000" />
                </Button>
              </div>
            ))}
          </URLsContainer>
        </Show>
      </Show>
    </div>
  )
}

const ErrorText = styled(FormText)`
  color: #dc3545 !important;
`

const URLsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 5px;

  a {
    overflow-wrap: anywhere;
  }

  div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  button {
    padding: 0.5rem 0.2rem;
  }
`

export default URLAttributeInput
